import type { ReactNode } from "react"
import { toast as sonnerToast } from "sonner"

export type Toast = {
  variant?: "default" | "destructive" | "success"
  title?: ReactNode
  description?: ReactNode
  duration?: number
  action?: { label: ReactNode; onClick: () => void }
}

// Deliberately narrow: regular toasts stay at the Toaster's default
// (bottom-right); only the announce flavor pins to top-center.
type ToastPosition = "top-center"

function sendToast(
  id: string | number | undefined,
  toastProps: Toast,
  position?: ToastPosition,
) {
  const { variant, title, description, duration, action } = toastProps
  const options = { id, description, duration, position, action }

  const newId =
    variant === "destructive"
      ? sonnerToast.error(title, options)
      : variant === "success"
        ? sonnerToast.success(title, options)
        : sonnerToast(title, options)

  return {
    id: newId,
    dismiss: () => sonnerToast.dismiss(newId),
    update: (newProps: Toast) => sendToast(newId, newProps, position),
  }
}

function toast(props: Toast) {
  return sendToast(undefined, props)
}

/**
 * Prominent toast rendered top-center (regular toasts are bottom-right).
 * Use for app-wide announcements that should interrupt attention.
 */
function announceToast(props: Toast) {
  return sendToast(undefined, props, "top-center")
}

export { toast, announceToast }
